<template>
    <div class="autocomplete-select" ref="root" @click="focarInput">
        <span v-if="bloqueado" class="autocomplete-select__valor" :title="textoExibido">
            {{ textoExibido || '—' }}
        </span>
        <template v-else>
            <input ref="inputEl" type="text" class="autocomplete-select__input" v-model="textoInput"
                :placeholder="placeholderAtual" @focus="onFocus" @blur="fecharLista" @input="onInput"
                @keydown.escape.prevent="fecharListaImediato" @keydown.down.prevent="abrirLista" />
            <ul v-if="mostrarLista" class="lista" :style="{ width: inputWidth + 'px' }">
                <li v-for="item in opcoesFiltradas" :key="item[valueKey]" @mousedown.prevent="selecionar(item)">
                    {{ item[labelKey] }}
                </li>
                <li v-if="opcoesFiltradas.length === 0" class="lista-vazia">Nenhum resultado</li>
            </ul>
        </template>
    </div>
</template>

<script>
export default {
    name: 'AutoCompleteSelect',

    props: {
        modelValue: {
            default: null,
        },
        items: {
            type: Array,
            default: () => [],
        },
        labelKey: {
            type: String,
            default: 'nomeCompleto',
        },
        valueKey: {
            type: String,
            default: 'id',
        },
        placeholder: {
            type: String,
            default: '',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        fallbackLabel: {
            type: String,
            default: '',
        },
    },

    emits: ['update:modelValue', 'change'],

    data() {
        return {
            mostrarLista: false,
            textoInput: '',
            textoExibido: '',
            editando: false,
            inputWidth: 0,
        };
    },

    computed: {
        bloqueado() {
            return this.disabled || this.readonly;
        },
        placeholderAtual() {
            if (this.placeholder) return this.placeholder;
            if (this.items.length === 0) return 'Sem opções';
            return 'Selecionar...';
        },
        opcoesFiltradas() {
            const termo = (this.textoInput || '').trim().toLowerCase();
            if (!termo || termo === (this.textoExibido || '').toLowerCase()) {
                return this.items;
            }
            return this.items.filter((item) => {
                const label = String(item?.[this.labelKey] ?? '').toLowerCase();
                return label.includes(termo);
            });
        },
    },

    watch: {
        modelValue: {
            immediate: true,
            handler() {
                if (!this.editando) {
                    this.sincronizarTexto();
                }
            },
        },
        items: {
            immediate: true,
            deep: true,
            handler() {
                if (!this.editando) {
                    this.sincronizarTexto();
                }
            },
        },
        fallbackLabel() {
            if (!this.editando) {
                this.sincronizarTexto();
            }
        },
    },

    mounted() {
        this.atualizarLargura();
        window.addEventListener('resize', this.atualizarLargura);
        this.$nextTick(() => this.sincronizarTexto());
    },

    beforeUnmount() {
        window.removeEventListener('resize', this.atualizarLargura);
    },

    methods: {
        atualizarLargura() {
            this.$nextTick(() => {
                if (this.$refs.inputEl) {
                    this.inputWidth = this.$refs.inputEl.offsetWidth;
                } else if (this.$refs.root) {
                    this.inputWidth = this.$refs.root.offsetWidth;
                }
            });
        },
        resolverTexto() {
            if (this.modelValue != null && this.modelValue !== '') {
                const item = this.items.find(
                    (i) => String(i?.[this.valueKey]) === String(this.modelValue),
                );
                if (item?.[this.labelKey]) {
                    return item[this.labelKey];
                }
            }
            return this.fallbackLabel || '';
        },
        sincronizarTexto() {
            const texto = this.resolverTexto();
            this.textoExibido = texto;
            if (!this.bloqueado) {
                this.textoInput = texto;
            }
        },
        focarInput() {
            if (this.bloqueado) return;
            this.$refs.inputEl?.focus();
        },
        onFocus() {
            if (this.bloqueado) return;
            this.editando = true;
            this.mostrarLista = true;
            this.atualizarLargura();
            this.$nextTick(() => {
                this.$refs.inputEl?.select();
            });
        },
        onInput() {
            if (this.bloqueado) return;
            this.editando = true;
            this.mostrarLista = true;
        },
        abrirLista() {
            if (this.bloqueado) return;
            this.mostrarLista = true;
        },
        selecionar(item) {
            const valor = item?.[this.valueKey] ?? null;
            const texto = item?.[this.labelKey] ?? '';
            this.textoInput = texto;
            this.textoExibido = texto;
            this.editando = false;
            this.mostrarLista = false;
            this.$emit('update:modelValue', valor);
            this.$emit('change', valor);
        },
        fecharListaImediato() {
            this.mostrarLista = false;
            this.editando = false;
            this.sincronizarTexto();
        },
        fecharLista() {
            setTimeout(() => {
                this.mostrarLista = false;
                this.editando = false;
                this.sincronizarTexto();
            }, 200);
        },
    },
};
</script>

<style scoped>
.autocomplete-select {
    position: relative;
    width: 100%;
    min-width: 10rem;
    text-align: left;
}

.autocomplete-select__input {
    width: 100%;
    box-sizing: border-box;
    cursor: text;
    text-align: left;
    height: 48px;
}

.autocomplete-select__valor {
    display: block;
    width: 100%;
    padding: 6px 0;
    color: var(--cor-fonte);
    line-height: 1.4;
    word-break: break-word;
    text-align: left;
}

.lista {
    background-color: var(--cor-bg);
    z-index: 99999;
    position: absolute;
    left: 0;
    top: calc(100% + 2px);
    margin: 0;
    padding: 0;
    list-style-type: none;
    color: var(--cor-fonte);
    max-height: 14rem;
    overflow-y: auto;
    overflow-x: hidden;
    border: 1px solid var(--cor-separador);
    border-radius: 6px;
    box-shadow: 0 4px 12px var(--cor-cinza);
    text-align: left;
}

.lista li {
    padding: 0.5rem 0.75rem;
    cursor: pointer;
    border-bottom: 1px solid var(--cor-separador);
    background-color: var(--cor-bg);
    text-align: left;
}

.lista li:last-child {
    border-bottom: none;
}

.lista li:hover:not(.lista-vazia) {
    background-color: var(--cor-primaria-fraca);
}

.lista-vazia {
    cursor: default;
    color: var(--cor-fonte-fraca);
    font-style: italic;
}
</style>
